/**
 * livretParcoursAM.js — Livret pédagogique maître Permis AM · Topolino.
 *
 * Aligné sur l'arrêté du 8 novembre 2012 modifié (annexe II) :
 * 5 séquences officielles, 8 h minimum, validation binaire
 * « compétence acquise / compétence à renforcer » à l'issue de S2 et S4.
 *
 * Fiche standard par thème : Objectif · Je comprends · Pourquoi · Risques ·
 * Influences · Exercice · QCM · Question ouverte · Réflexe sécurité.
 */

export const SEQUENCES = [
  { id: 's1', num: 1, emoji: '🟦', color: '#0ea5e9', title: 'Je parle de ma conduite',              subtitle: 'Représentations et attentes' },
  { id: 's2', num: 2, emoji: '🟩', color: '#10b981', title: 'Je découvre et je maîtrise la Topolino',subtitle: 'Hors circulation' },
  { id: 's3', num: 3, emoji: '🟨', color: '#eab308', title: 'Je connais les règles',                subtitle: 'Signalisation · priorités · positionnement' },
  { id: 's4', num: 4, emoji: '🟥', color: '#e11d48', title: 'Je circule',                            subtitle: 'Observation · anticipation · partage de la route' },
  { id: 's5', num: 5, emoji: '🟧', color: '#f97316', title: 'Je comprends et je gère les risques',   subtitle: 'Vitesse · fatigue · téléphone · influences' },
];

// Influences standard réutilisable si non spécifiées sur un thème donné
const INFLUENCES_STD = {
  moi: ['fatigue', 'stress', 'émotions', 'confiance excessive', 'manque d\'expérience'],
  autres: ['copains', 'passager', 'véhicule qui suit', 'pression du groupe'],
  environnement: ['pluie / brouillard', 'nuit', 'circulation dense', 'travaux'],
  vehicule: ['autonomie batterie', 'vitesse limitée à 45 km/h', 'freinage voiturette'],
};

// ─── FICHES ─────────────────────────────────────────────────────────────────
export const FICHES = [
  // ─── SÉQUENCE 1 ──────────────────────────────────────────────────────────
  {
    id: 't01', num: 1, sequence: 's1', emoji: '💭', title: 'Mes représentations',
    objectif: "Identifier ce que je pense savoir sur la conduite avant de commencer.",
    comprendre: [
      "Je peux avoir déjà vu conduire mes parents, mes amis ou d'autres personnes.",
      "Mais observer quelqu'un conduire ne signifie pas savoir conduire soi-même.",
      "Conduire demande des connaissances, des compétences et de l'expérience.",
    ],
    pourquoi: [
      "Une situation peut paraître simple alors qu'elle demande plusieurs actions :",
      "observer → comprendre → décider → agir.",
    ],
    risques: ["surestimer mes capacités", "sous-estimer une situation", "reproduire une mauvaise habitude", "penser que la voiturette est \"facile\" parce qu'elle est petite"],
    influences: { moi: ['confiance', 'manque d\'expérience', 'envie de faire comme les autres'], autres: ['amis', 'famille', 'réseaux sociaux'], environnement: ['tendances vues en ligne'], vehicule: [] },
    question: "Qu'est-ce qui te paraît le plus difficile dans la conduite ?",
    qcm: [
      { q: "Avoir déjà vu quelqu'un conduire signifie que je sais conduire.", choix: ['Vrai', 'Faux'], bonne: 'B' },
      { q: "Pour conduire correctement, je dois :", choix: ['seulement connaître les panneaux', 'observer, décider et agir', 'suivre les autres'], bonne: 'B' },
    ],
    reflexe: "OBSERVER ≠ SAVOIR CONDUIRE",
  },

  // ─── SÉQUENCE 2 ──────────────────────────────────────────────────────────
  {
    id: 't02', num: 2, sequence: 's2', emoji: '🚗', title: 'Découvrir le véhicule',
    objectif: "Identifier les principaux éléments de la Topolino et comprendre leur rôle.",
    comprendre: ["Avant de conduire, je dois connaître mon véhicule : où sont les commandes, comment démarrer, avancer, reculer, freiner, signaler, contrôler."],
    pourquoi: ["En situation de conduite, je dois pouvoir utiliser les commandes sans chercher longtemps."],
    risques: ["une mauvaise réaction", "une perte de contrôle", "une distraction", "une mauvaise communication avec les autres"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Sans aide, je montre :', items: ['frein', 'accélérateur', 'volant', 'sélecteur', 'clignotants', 'feux', 'avertisseur sonore', 'essuie-glaces', 'frein de stationnement', 'rétroviseurs'] },
    qcm: [{ q: "Pourquoi doit-on connaître les commandes avant de circuler ?", choix: ['Pour aller plus vite', 'Pour éviter de chercher les commandes pendant la conduite', 'Pour économiser la batterie'], bonne: 'B' }],
    reflexe: "JE REPÈRE → JE MAÎTRISE",
  },
  {
    id: 't03', num: 3, sequence: 's2', emoji: '🪑', title: "Bien s'installer",
    objectif: "Adopter une position permettant de conduire et d'observer correctement.",
    comprendre: ["Je règle mon siège, mon volant (si nécessaire), mes rétroviseurs, ma ceinture."],
    pourquoi: ["Une bonne position facilite les mouvements, le freinage, la direction, la visibilité et l'utilisation des commandes."],
    risques: ["mauvaise visibilité", "mauvaise maîtrise", "mauvais positionnement", "difficulté à réagir"],
    influences: { moi: ['précipitation', 'envie de partir tout de suite'], autres: ['amis qui attendent', 'passager pressé'], environnement: ['stress avant départ'], vehicule: ['siège / rétros mal réglés par la personne précédente'] },
    exercice: { titre: 'Préparer entièrement la Topolino sans intervention', items: ['Réglage siège', 'Rétroviseurs', 'Ceinture', 'Commandes', 'Environnement'] },
    qcm: [{ q: "La première chose à faire avant de partir est :", choix: ['accélérer', "s'installer correctement et contrôler", 'mettre la musique'], bonne: 'B' }],
    reflexe: "M'INSTALLER → RÉGLER → VÉRIFIER → PARTIR",
  },
  {
    id: 't04', num: 4, sequence: 's2', emoji: '🔍', title: 'Les contrôles avant départ',
    objectif: "Vérifier que le véhicule et son environnement permettent de démarrer en sécurité.",
    comprendre: ["Je contrôle : état général, pneumatiques, visibilité, environnement immédiat, portes, ceinture, rétroviseurs."],
    pourquoi: ["Une situation dangereuse peut exister avant même que le véhicule commence à rouler."],
    risques: ["défaillance véhicule non détectée", "obstacle proche non repéré", "usager vulnérable dans l'angle mort"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Pourquoi regarder autour du véhicule avant de partir ?", choix: ['Pour gagner du temps', "Pour identifier un danger éventuel", 'Pour vérifier la couleur de la voiture'], bonne: 'B' }],
    reflexe: "AVANT DE ROULER · JE VÉRIFIE",
  },
  {
    id: 't05', num: 5, sequence: 's2', emoji: '🔑', title: 'Démarrer',
    objectif: "Démarrer sans créer de danger.",
    comprendre: ["Méthode : OBSERVATION → CONTRÔLE → ACTION. Je contrôle l'environnement, je vérifie ma direction, je démarre progressivement, je contrôle immédiatement ma trajectoire."],
    pourquoi: ["Le démarrage est une phase où je peux facilement créer un danger si je manque de contrôle."],
    risques: ["obstacle", "piéton", "véhicule", "mauvaise trajectoire", "accélération excessive"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Avant de démarrer, je dois :", choix: ['regarder uniquement devant', "contrôler mon environnement", 'regarder mon téléphone'], bonne: 'B' }],
    reflexe: "OBSERVER · CONTRÔLER · AGIR",
  },
  {
    id: 't06', num: 6, sequence: 's2', emoji: '⚡', title: "Accélérer et maîtriser l'allure",
    objectif: "Adapter mon allure à la situation.",
    comprendre: ["La vitesse influence : le temps disponible pour réagir, la distance nécessaire pour s'arrêter, la gravité potentielle d'un choc, ma capacité à observer."],
    pourquoi: ["La vitesse n'est pas seulement un chiffre — c'est un facteur de risque quand elle n'est pas adaptée."],
    risques: ["freinage tardif", "perte de contrôle", "mauvaise observation"],
    influences: { moi: ['envie de rouler vite', 'confiance excessive'], autres: ['voiture derrière', 'amis dans le véhicule'], environnement: ['route dégagée qui donne envie d\'accélérer'], vehicule: ['limitation 45 km/h par construction'] },
    qcm: [{ q: "Une vitesse adaptée dépend :", choix: ['uniquement de la limitation', "de la situation de circulation", 'uniquement de mon envie'], bonne: 'B' }],
    reflexe: "45 = LIMITE, JAMAIS UN OBJECTIF",
  },
  {
    id: 't07', num: 7, sequence: 's2', emoji: '🛑', title: "Freiner et s'arrêter",
    objectif: "Ralentir et m'immobiliser avec précision.",
    comprendre: ["Plus je roule vite, plus je dois anticiper mon arrêt."],
    pourquoi: ["Anticiper évite le freinage tardif et donne le temps de mieux gérer la situation."],
    risques: ["freinage tardif", "obstacle", "intersection", "piéton", "véhicule devant", "chaussée glissante"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Exercices progressifs', items: ['arrêt doux', 'arrêt précis', 'freinage plus appuyé', 'arrêt avant une ligne', 'arrêt en situation simulée'] },
    qcm: [{ q: "Pour avoir plus de temps pour réagir, je peux :", choix: ['accélérer', "anticiper et adapter mon allure", 'regarder uniquement le véhicule devant'], bonne: 'B' }],
    reflexe: "ANTICIPER > FREINER TARD",
  },
  {
    id: 't08', num: 8, sequence: 's2', emoji: '↩️', title: 'Diriger / tourner',
    objectif: "Maintenir une trajectoire maîtrisée.",
    comprendre: ["Le regard influence la trajectoire. Je regarde là où je veux aller tout en conservant une vision globale de mon environnement."],
    pourquoi: ["Le regard mal placé conduit à des trajectoires imprécises et à des corrections brutales."],
    risques: ["virage pris trop vite", "regard fixé sur un obstacle", "trajectoire trop large", "correction brutale"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Dans un virage, mon regard doit :", choix: ['rester fixé sur le bord de la route', "accompagner la trajectoire et rechercher la sortie", 'regarder uniquement le capot'], bonne: 'B' }],
    reflexe: "REGARDE OÙ TU VEUX ALLER",
  },
  {
    id: 't09', num: 9, sequence: 's2', emoji: '↙️', title: 'Marche arrière',
    objectif: "Effectuer une marche arrière lente et contrôlée.",
    comprendre: ["La visibilité vers l'arrière est limitée. Je dois contrôler mon environnement avant et pendant la manœuvre."],
    pourquoi: ["Le manque de visibilité en marche arrière est une des principales causes d'accrochages."],
    risques: ["piéton non vu", "obstacle bas", "autre véhicule", "mauvaise trajectoire"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Je fais', items: ['contrôle', 'marche arrière lente', 'regard', 'rétroviseurs', 'arrêt si doute'] },
    qcm: [{ q: "Si je ne suis plus certain de ce qui se trouve derrière :", choix: ['je continue', 'j\'accélère', "je m'arrête et je contrôle"], bonne: 'C' }],
    reflexe: "DOUTE → STOP · CONTRÔLE",
  },
  {
    id: 't10', num: 10, sequence: 's2', emoji: '🅿️', title: 'Stationner',
    objectif: "Maîtriser les manœuvres de stationnement.",
    comprendre: ["Je travaille bataille, épi et créneau. Une manœuvre lente reste une situation où je dois contrôler mon environnement."],
    pourquoi: ["Le stationnement génère beaucoup de petits accidents parce qu'on baisse la vigilance."],
    risques: ["piéton", "obstacle", "autre véhicule", "mauvaise appréciation des distances"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Pendant une manœuvre :", choix: ['je peux arrêter de regarder autour de moi', "je dois continuer à contrôler mon environnement", 'je dois accélérer'], bonne: 'B' }],
    reflexe: "MÊME EN MANŒUVRE · JE CONTRÔLE",
  },

  // ─── SÉQUENCE 3 ──────────────────────────────────────────────────────────
  {
    id: 't11', num: 11, sequence: 's3', emoji: '🚸', title: 'La signalisation',
    objectif: "Identifier les principaux panneaux et comprendre leur rôle.",
    comprendre: ["Je dois savoir reconnaître : interdiction, obligation, danger, indication, priorité, marquage au sol."],
    pourquoi: ["La signalisation permet d'organiser et de sécuriser les déplacements."],
    risques: ["mauvaise interprétation", "panneau non vu", "règle non respectée"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "La signalisation sert notamment à :", choix: ['décorer la route', "informer, avertir, interdire ou imposer", 'ralentir uniquement les voiturettes'], bonne: 'B' }],
    reflexe: "JE LIS LA ROUTE",
  },
  {
    id: 't12', num: 12, sequence: 's3', emoji: '📍', title: 'Positionnement',
    objectif: "Choisir une position adaptée à la situation.",
    comprendre: ["Je choisis ma position : en ligne droite, dans un virage, lors d'un changement de direction, à l'approche d'une intersection."],
    pourquoi: ["Un mauvais positionnement peut surprendre les autres, réduire les marges de sécurité et compliquer un changement de direction."],
    risques: ["mauvaise anticipation par les autres", "trajectoire dangereuse", "conflit avec un autre usager"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Mon positionnement doit tenir compte :", choix: ["de la situation et de ma trajectoire", 'uniquement du véhicule derrière', 'uniquement de la largeur de la route'], bonne: 'A' }],
    reflexe: "OÙ JE SUIS DIT CE QUE JE VAIS FAIRE",
  },
  {
    id: 't13', num: 13, sequence: 's3', emoji: '🚦', title: 'Intersections et priorités',
    objectif: "Appliquer la méthode aux intersections : ralentir → observer → identifier la règle → décider → s'engager.",
    comprendre: ["Une intersection est une zone où les trajectoires de plusieurs usagers peuvent se croiser."],
    pourquoi: ["Le refus de priorité est une cause majeure d'accidents graves."],
    risques: ["véhicule masqué", "refus de priorité", "mauvaise lecture de la signalisation", "vitesse excessive"],
    influences: { moi: ['précipitation'], autres: ['véhicule derrière qui pousse'], environnement: ['visibilité réduite'], vehicule: [] },
    note: "Un véhicule derrière peut me pousser psychologiquement à m'engager trop vite. Je ne laisse jamais la pression d'un autre usager décider à ma place.",
    qcm: [{ q: "Si j'ai un doute à une intersection :", choix: ['je m\'engage rapidement', "je ralentis et je recherche davantage d'informations", 'je suis le véhicule devant'], bonne: 'B' }],
    reflexe: "DOUTE = JE CÈDE",
  },

  // ─── SÉQUENCE 4 ──────────────────────────────────────────────────────────
  {
    id: 't14', num: 14, sequence: 's4', emoji: '👀', title: 'Regarder',
    objectif: "Rechercher les informations utiles.",
    comprendre: ["Je cherche : loin, proche, côtés, rétroviseurs, intersections, usagers vulnérables, obstacles."],
    pourquoi: ["Sans regard actif, aucune anticipation n'est possible."],
    risques: ["information manquée", "réaction tardive"],
    influences: INFLUENCES_STD,
    question: "Qu'est-ce qui pourrait arriver dans les prochaines secondes ?",
    reflexe: "LOIN · LARGE · MOBILE",
  },
  {
    id: 't15', num: 15, sequence: 's4', emoji: '🧠', title: 'Observer et anticiper',
    objectif: "Analyser une situation avant d'agir.",
    comprendre: ["Formule : Je vois → j'analyse → j'anticipe → j'agis."],
    pourquoi: ["Anticiper évite d'avoir à réagir en urgence."],
    risques: ["situation non anticipée = manœuvre d'urgence"],
    influences: INFLUENCES_STD,
    exercice: { titre: "À chaque situation, l'élève verbalise :", items: ['Ce que je vois', 'Le risque possible', 'Ce que je vais faire'] },
    reflexe: "JE VOIS · J'ANALYSE · J'ANTICIPE · J'AGIS",
  },
  {
    id: 't16', num: 16, sequence: 's4', emoji: '📢', title: 'Communiquer',
    objectif: "Rendre mes intentions compréhensibles aux autres usagers.",
    comprendre: ["Je communique par : positionnement, clignotant, ralentissement, feux, regard, comportement."],
    pourquoi: ["Les autres usagers ne peuvent pas deviner mes intentions."],
    risques: ["intention non lisible → conflit"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Le clignotant :", choix: ['remplace les contrôles', "indique mon intention mais ne remplace pas l'observation", 'me donne automatiquement la priorité'], bonne: 'B' }],
    reflexe: "JE FAIS COMPRENDRE CE QUE JE VAIS FAIRE",
  },
  {
    id: 't17', num: 17, sequence: 's4', emoji: '🔄', title: 'Giratoire',
    objectif: "Aborder, circuler et sortir d'un giratoire en sécurité.",
    comprendre: ["Avant : je ralentis, j'observe, j'identifie les usagers. Pendant : j'adapte mon allure, je contrôle, je communique. Sortie : je signale, je vérifie les usagers vulnérables."],
    pourquoi: ["Un giratoire concentre plusieurs risques : entrées multiples, différences de vitesse, mauvaise lisibilité."],
    risques: ["entrée trop rapide", "mauvaise observation", "oubli d'un usager", "mauvaise communication"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Avant d'entrer dans un giratoire :", choix: ['j\'accélère', "j'observe et j'adapte mon allure", 'je regarde uniquement à gauche'], bonne: 'B' }],
    reflexe: "CÉDER · CLIGNOTER · GARDER MA TRAJECTOIRE",
  },
  {
    id: 't18', num: 18, sequence: 's4', emoji: '📊', title: 'Différence de vitesse',
    objectif: "Comprendre que rouler à 45 km/h peut me rendre beaucoup plus lent que les autres.",
    comprendre: ["La Topolino est limitée à 45 km/h. D'autres véhicules peuvent circuler plus rapidement. Je peux être légalement à ma vitesse maximale tout en étant beaucoup plus lent qu'un autre véhicule."],
    pourquoi: ["Cette différence est une source de pression et parfois de comportements dangereux des autres usagers."],
    risques: ["véhicule qui arrive rapidement derrière", "dépassement", "pression", "mauvaise appréciation"],
    influences: { moi: ['pression', 'envie de céder'], autres: ['conducteur qui colle', 'coup de klaxon', 'dépassement brusque'], environnement: ['route étroite'], vehicule: ['limitation par construction'] },
    note: "Je reste prévisible. Je garde ma trajectoire. Je n'accélère pas pour faire plaisir à l'autre.",
    qcm: [{ q: "Un véhicule me suit de très près :", choix: ['j\'accélère au-delà des capacités du véhicule', "je reste maître de ma conduite et j'adapte mon comportement", 'je freine volontairement pour le gêner'], bonne: 'B' }],
    reflexe: "PRÉVISIBLE · MA TRAJECTOIRE · MA SÉCURITÉ",
  },
  {
    id: 't19', num: 19, sequence: 's4', emoji: '↔️', title: 'Dépasser / être dépassé',
    objectif: "Rester prévisible et stable lors d'un dépassement.",
    comprendre: ["Le dépassement crée une différence de vitesse et réduit temporairement les marges latérales."],
    pourquoi: ["C'est un moment de risque élevé pour les deux véhicules."],
    risques: ["véhicule arrivant en face", "obstacle", "cycliste", "mauvaise visibilité", "dépassement trop proche"],
    influences: INFLUENCES_STD,
    reflexe: "PRÉVISIBLE · STABLE · JE NE ME LAISSE PAS DÉSTABILISER",
  },
  {
    id: 't20', num: 20, sequence: 's4', emoji: '🚶', title: 'Piétons',
    objectif: "Anticiper les déplacements imprévisibles des piétons.",
    comprendre: ["Je recherche : enfants, personnes âgées, personnes masquées par un véhicule, piétons proches d'un passage, personnes susceptibles de traverser."],
    pourquoi: ["Un piéton peut changer de direction rapidement."],
    risques: ["piéton masqué", "piéton distrait", "traversée hors passage"],
    influences: INFLUENCES_STD,
    note: "Je ralentis avant d'être obligé de freiner.",
    qcm: [{ q: "Un véhicule stationné masque un enfant :", choix: ['je maintiens mon allure', "j'anticipe et j'adapte mon allure", 'je klaxonne et accélère'], bonne: 'B' }],
    reflexe: "PIÉTON = PRIORITÉ · JE RALENTIS",
  },
  {
    id: 't21', num: 21, sequence: 's4', emoji: '🚴', title: 'Cyclistes et deux-roues',
    objectif: "Anticiper la vulnérabilité des cyclistes et deux-roues.",
    comprendre: ["Un deux-roues est plus vulnérable qu'une voiturette. Il peut être déporté par un obstacle, un vent latéral, une portière, une chaussée dégradée."],
    pourquoi: ["Sa trajectoire peut évoluer très vite alors qu'il est peu visible."],
    risques: ["dépassement trop proche", "ouverture de portière", "changement de trajectoire", "intersection"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "Je dois considérer un cycliste comme :", choix: ['un obstacle immobile', "un usager dont la trajectoire peut évoluer", 'un véhicule que je peux toujours dépasser immédiatement'], bonne: 'B' }],
    reflexe: "DISTANCE 1M50 · JE LE VOIS DE LOIN",
  },
  {
    id: 't22', num: 22, sequence: 's4', emoji: '🚫', title: 'Angles morts',
    objectif: "Comprendre qu'un usager peut être difficilement visible.",
    comprendre: ["Ne pas voir ≠ absence de danger."],
    pourquoi: ["Un usager caché dans mon angle mort peut surgir à tout moment."],
    risques: ["cycliste dans l'angle mort", "piéton entre voitures", "moto qui remonte les files"],
    influences: INFLUENCES_STD,
    exercice: { titre: "L'enseignant montre plusieurs situations où un usager disparaît. L'élève explique :", items: ['Où pourrait-il se trouver ?'] },
    reflexe: "SI JE NE VOIS PAS · JE SUPPOSE QU'IL EST LÀ",
  },
  {
    id: 't23', num: 23, sequence: 's4', emoji: '🚛', title: 'Véhicules lourds',
    objectif: "Gérer la proximité et les angles morts des poids lourds.",
    comprendre: ["Les poids lourds ont de larges angles morts, un encombrement important, des trajectoires larges."],
    pourquoi: ["Rester invisible aux yeux du chauffeur = danger direct."],
    risques: ["angles morts", "encombrement", "trajectoires larges", "déplacement d'air", "difficulté à voir le conducteur"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Mon comportement', items: ['je garde mes distances', "j'évite de rester dans une zone invisible", "j'anticipe les changements de trajectoire"] },
    reflexe: "S'IL NE ME VOIT PAS · JE SUIS EN DANGER",
  },

  // ─── SÉQUENCE 5 ──────────────────────────────────────────────────────────
  {
    id: 't24', num: 24, sequence: 's5', emoji: '📊', title: 'La vitesse',
    objectif: "Comprendre l'impact de la vitesse.",
    comprendre: ["La vitesse influence : temps de réaction + distance de freinage + gravité potentielle du choc."],
    pourquoi: ["Chaque km/h en plus multiplie l'énergie à dissiper en cas de choc."],
    risques: ["manque de temps", "freinage tardif", "mauvaise observation", "perte de contrôle"],
    influences: { moi: ['envie de vitesse'], autres: ['pression des autres', 'défi entre amis'], environnement: ['impression que la route est sûre'], vehicule: [] },
    question: "Pourquoi une vitesse faible peut-elle parfois être nécessaire même si la limitation autorise davantage ?",
    reflexe: "45 = LIMITE · JAMAIS UN OBJECTIF",
  },
  {
    id: 't25', num: 25, sequence: 's5', emoji: '📏', title: 'Distances de sécurité',
    objectif: "Conserver une marge suffisante pour réagir.",
    comprendre: ["Je dois conserver une marge suffisante pour réagir si le véhicule devant ralentit ou s'arrête. La distance doit augmenter si : visibilité réduite, chaussée glissante, circulation dense, vitesse plus élevée, fatigue ou attention diminuée."],
    pourquoi: ["Sans marge, aucun réflexe humain ne suffit à éviter un choc arrière."],
    risques: ["choc par l'arrière", "impossibilité de freiner à temps"],
    influences: INFLUENCES_STD,
    qcm: [{ q: "La distance de sécurité sert principalement à :", choix: ['laisser passer les motos', "disposer d'un espace pour réagir", 'rouler plus vite'], bonne: 'B' }],
    reflexe: "2 SECONDES MINIMUM · X2 SI MAUVAIS TEMPS",
  },
  {
    id: 't26', num: 26, sequence: 's5', emoji: '🌧️', title: 'Météo',
    objectif: "Adapter ma conduite à la météo.",
    comprendre: [
      "Pluie : adhérence réduite, visibilité réduite, freinage plus délicat, projections.",
      "Brouillard : visibilité fortement réduite, anticipation plus difficile.",
      "Vent : trajectoire perturbée, attention aux véhicules et obstacles.",
    ],
    pourquoi: ["La météo change les distances, l'adhérence et la visibilité — donc les marges de sécurité."],
    risques: ["glissade", "aquaplaning", "perte de trajectoire"],
    influences: INFLUENCES_STD,
    reflexe: "JE VOIS MOINS · JE ROULE MOINS · MARGES × 2",
  },
  {
    id: 't27', num: 27, sequence: 's5', emoji: '🌙', title: 'Nuit et visibilité',
    objectif: "Circuler en sécurité en visibilité réduite.",
    comprendre: ["La nuit modifie la perception des distances, la visibilité des obstacles, la détection des usagers vulnérables."],
    pourquoi: ["Ma vision est ma principale source d'information — sans elle, je conduis en aveugle."],
    risques: ["obstacle non vu", "piéton non détecté", "éblouissement"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Je fais', items: ['feux adaptés', 'vitesse adaptée', 'observation renforcée', 'anticipation'] },
    reflexe: "MOINS JE VOIS · MOINS JE ROULE VITE",
  },
  {
    id: 't28', num: 28, sequence: 's5', emoji: '😴', title: 'Fatigue',
    objectif: "Reconnaître mes limites.",
    comprendre: ["Signes : bâillements, difficultés à rester concentré, erreurs répétées, regard moins efficace, réactions plus lentes."],
    pourquoi: ["La fatigue réduit les capacités nécessaires à la conduite."],
    risques: ["assoupissement", "réaction tardive", "erreur d'appréciation"],
    influences: INFLUENCES_STD,
    reflexe: "SIGNES DE FATIGUE = PAUSE OBLIGATOIRE",
  },
  {
    id: 't29', num: 29, sequence: 's5', emoji: '📱', title: 'Téléphone et distractions',
    objectif: "Rester 100 % concentré sur la conduite.",
    comprendre: ["Le téléphone peut détourner mes yeux, mes mains, mon attention."],
    pourquoi: ["Je peux ne pas voir une information importante alors même qu'elle se trouve devant moi."],
    risques: ["information cruciale manquée", "collision", "sortie de route"],
    influences: { moi: ['envie de répondre'], autres: ['pression des amis'], environnement: ['notification', 'message', 'appel'], vehicule: [] },
    qcm: [{ q: "Une notification arrive :", choix: ['je regarde rapidement', "je reste concentré sur la conduite", 'je prends le téléphone à un feu'], bonne: 'B' }],
    reflexe: "AU VOLANT · TÉLÉPHONE ÉTEINT OU MODE VOITURE",
  },
  {
    id: 't30', num: 30, sequence: 's5', emoji: '🚫', title: 'Alcool, drogues et médicaments',
    objectif: "Comprendre que certaines substances rendent la conduite dangereuse.",
    comprendre: ["Certaines substances peuvent modifier l'attention, les réflexes, la perception, la prise de décision. Certains médicaments aussi."],
    pourquoi: ["Le permis AM est en accompagnement d'un jeune conducteur — la marge d'erreur est nulle."],
    risques: ["accident", "sanctions pénales", "responsabilité en cas de blessure"],
    influences: INFLUENCES_STD,
    reflexe: "0 ALCOOL · 0 DROGUE · TOUJOURS · ZÉRO EXCUSE",
  },
  {
    id: 't31', num: 31, sequence: 's5', emoji: '👥', title: 'Le passager',
    objectif: "Rester maître de ma conduite quel que soit le comportement de mon passager.",
    comprendre: ["Un passager peut influencer ma conduite."],
    pourquoi: ["Le conducteur est le seul responsable, quoi qu'en dise le passager."],
    risques: ["distraction", "prise de risque pour impressionner", "mauvaise décision sous pression"],
    influences: { moi: ['envie d\'impressionner'], autres: ['discussion', 'musique', 'téléphone', 'encouragement à aller plus vite'], environnement: [], vehicule: [] },
    question: "Si mon passager me demande d'aller plus vite, qui reste responsable de ma conduite ?",
    reflexe: "JE RESTE LE CONDUCTEUR · JE DÉCIDE",
  },
  {
    id: 't32', num: 32, sequence: 's5', emoji: '😰', title: 'Pression des pairs',
    objectif: "Résister à la pression du groupe.",
    comprendre: ["Un ami dit : \"Vas-y, accélère !\". Je dois être capable de : dire non, conserver mon allure, respecter les règles, ne pas chercher à impressionner."],
    pourquoi: ["Céder à la pression = mettre ma sécurité entre les mains de quelqu'un d'autre."],
    risques: ["accident", "perte de permis", "conflit intérieur après coup"],
    influences: { moi: ['peur de passer pour un peureux'], autres: ['défi', 'moquerie', 'insistance'], environnement: [], vehicule: [] },
    note: "Je conduis pour arriver en sécurité, pas pour impressionner.",
    qcm: [{ q: "Un ami me pousse à prendre un risque :", choix: ['je le fais pour ne pas passer pour un peureux', "je garde ma décision de sécurité", 'je suis le groupe'], bonne: 'B' }],
    reflexe: "MES CHOIX · MA SÉCURITÉ · MA VIE",
  },
  {
    id: 't33', num: 33, sequence: 's5', emoji: '💭', title: 'Émotions et stress',
    objectif: "Prendre conscience de l'impact des émotions.",
    comprendre: ["La colère, le stress, la peur ou l'excitation peuvent modifier ma manière de conduire."],
    pourquoi: ["Les émotions court-circuitent la réflexion et poussent à l'action impulsive."],
    risques: ["décisions plus rapides", "mauvaise observation", "comportement agressif", "prise de risque"],
    influences: INFLUENCES_STD,
    reflexe: "AVANT D'AGIR · JE RESPIRE",
  },
  {
    id: 't34', num: 34, sequence: 's5', emoji: '🔋', title: 'Autonomie électrique',
    objectif: "Anticiper mon autonomie et mes recharges.",
    comprendre: ["L'autonomie dépend de : vitesse, conditions de circulation, température, relief, style de conduite, utilisation du véhicule. Données Topolino : 45 km/h max, 75 km d'autonomie WMTC, recharge 4 h sur prise standard."],
    pourquoi: ["Une batterie faible peut devenir un problème si je n'ai pas anticipé mon trajet."],
    risques: ["panne d'énergie", "besoin de recharge inattendu"],
    influences: INFLUENCES_STD,
    exercice: { titre: 'Je prépare', items: ['trajet', 'distance', 'possibilité de recharge', 'marge restante'] },
    note: "Je ne planifie pas un trajet en utilisant toute l'autonomie disponible.",
    qcm: [{ q: "Si mon autonomie affichée est faible :", choix: ['j\'attends d\'être à 0 km', "j'anticipe la recharge", 'j\'accélère pour arriver plus vite'], bonne: 'B' }],
    reflexe: "MARGE 20 % · JE PLANIFIE MES RECHARGES",
  },
  {
    id: 't35', num: 35, sequence: 's5', emoji: '🎯', title: 'Analyser une situation',
    objectif: "Appliquer la méthode des 5 questions.",
    comprendre: [
      "1. QUE VOIS-JE ?",
      "2. QUE NE VOIS-JE PAS ?",
      "3. QUEL EST LE RISQUE ?",
      "4. QU'EST-CE QUI PEUT M'INFLUENCER ?",
      "5. QUE VAIS-JE FAIRE ?",
    ],
    pourquoi: ["Cette méthode standardise la prise de décision et évite les oublis."],
    risques: [],
    influences: INFLUENCES_STD,
    reflexe: "5 QUESTIONS · À CHAQUE FOIS",
  },
  {
    id: 't36', num: 36, sequence: 's5', emoji: '🎬', title: 'Mises en situation',
    objectif: "Verbaliser une réponse construite à des scénarios réels.",
    comprendre: ["Pour chaque situation je réponds : Que fais-tu ? Pourquoi ?"],
    scenarios: [
      "Tu arrives à une intersection. Un véhicule est stationné et masque une partie de la route. Que fais-tu ? Pourquoi ?",
      "Tu circules à 45 km/h. Un véhicule arrive rapidement derrière toi. Que fais-tu ? Pourquoi ?",
      "Un ami te demande d'accélérer. Que réponds-tu ?",
      "Tu arrives dans un virage sous la pluie. Quels sont les risques ? Comment adaptes-tu ta conduite ?",
      "Tu disposes de peu d'autonomie et ton trajet prévu est encore long. Que fais-tu ?",
    ],
    pourquoi: ["La verbalisation force l'analyse et fixe les apprentissages."],
    risques: [],
    influences: INFLUENCES_STD,
    reflexe: "JE VERBALISE · JE JUSTIFIE · JE PROGRESSE",
  },
];

// ─── Évaluation finale (grille officielle Legifrance) ──────────────────────
export const EVAL_FINALE_GROUPS = [
  { id: 'ef-conduite', title: 'Je sais conduire la Topolino', items: [
    { id: 'ef-install', label: "Je m'installe correctement." },
    { id: 'ef-commandes', label: 'Je connais les commandes.' },
    { id: 'ef-demarrer', label: 'Je démarre.' },
    { id: 'ef-ralentir', label: 'Je ralentis.' },
    { id: 'ef-freiner', label: 'Je freine.' },
    { id: 'ef-arreter', label: "Je m'arrête." },
    { id: 'ef-diriger', label: 'Je dirige le véhicule.' },
    { id: 'ef-ma', label: 'Je réalise une marche arrière.' },
    { id: 'ef-stat', label: 'Je stationne.' },
  ] },
  { id: 'ef-observer', title: '👀 Je sais observer', items: [
    { id: 'ef-loin', label: 'Je regarde loin.' },
    { id: 'ef-retros', label: 'Je contrôle mes rétroviseurs.' },
    { id: 'ef-vuln', label: 'Je recherche les usagers vulnérables.' },
    { id: 'ef-masque', label: "J'identifie les zones masquées." },
    { id: 'ef-info', label: 'Je prends les informations utiles.' },
  ] },
  { id: 'ef-anticiper', title: '🧠 Je sais anticiper', items: [
    { id: 'ef-risques', label: 'Je recherche les risques.' },
    { id: 'ef-allure', label: 'J\'adapte mon allure.' },
    { id: 'ef-distance', label: 'Je garde des distances suffisantes.' },
    { id: 'ef-vitesse-autres', label: 'Je prends en compte la vitesse des autres.' },
    { id: 'ef-prevoir', label: 'Je prévois les comportements possibles des autres usagers.' },
  ] },
  { id: 'ef-communiquer', title: '📢 Je sais communiquer', items: [
    { id: 'ef-position', label: 'Je me positionne correctement.' },
    { id: 'ef-cligno', label: "J'utilise mes clignotants." },
    { id: 'ef-intentions', label: 'Je rends mes intentions compréhensibles.' },
    { id: 'ef-previs', label: 'Je reste prévisible.' },
  ] },
  { id: 'ef-risques', title: '⚠️ Je sais gérer les risques', items: [
    { id: 'ef-r-vitesse', label: 'Vitesse' },
    { id: 'ef-r-fatigue', label: 'Fatigue' },
    { id: 'ef-r-tel', label: 'Téléphone' },
    { id: 'ef-r-ad', label: 'Alcool / drogues / médicaments' },
    { id: 'ef-r-meteo', label: 'Météo' },
    { id: 'ef-r-nuit', label: 'Nuit' },
    { id: 'ef-r-pass', label: 'Passager' },
    { id: 'ef-r-pression', label: 'Pression des pairs' },
    { id: 'ef-r-diff', label: 'Différence de vitesse' },
    { id: 'ef-r-auto', label: 'Autonomie' },
  ] },
];

// ─── 10 réflexes sécurité finaux ───────────────────────────────────────────
export const REFLEXES = [
  { emoji: '👀', txt: 'Je regarde.' },
  { emoji: '🧠', txt: "J'anticipe." },
  { emoji: '🚗', txt: 'J\'adapte mon allure.' },
  { emoji: '📢', txt: 'Je communique.' },
  { emoji: '↔️', txt: 'Je garde mes distances.' },
  { emoji: '⚠️', txt: 'Je recherche les usagers vulnérables.' },
  { emoji: '🛑', txt: "Je ralentis si j'ai un doute." },
  { emoji: '📵', txt: 'Je reste concentré.' },
  { emoji: '👥', txt: 'Je ne me laisse pas influencer.' },
  { emoji: '🔋', txt: "J'anticipe mon autonomie." },
];

// ─── Grille enseignant officielle (S2 + S4) ────────────────────────────────
export const GRILLE_ENSEIGNANT = {
  s2: {
    title: 'Séquence 2 — Hors circulation',
    items: ['Installation', 'Commandes', 'Démarrage', 'Arrêt', 'Freinage', 'Direction', 'Demi-tour', 'Évitement', 'Marche arrière', 'Stationnement'],
  },
  s4: {
    title: 'Séquence 4 — Circulation',
    items: ["Démarrer / s'insérer", 'Ralentir / immobiliser', 'Tourner', 'Franchir une intersection', 'Changer de direction', 'Circuler en agglomération', 'Prendre en compte les autres usagers', 'Adapter son allure', 'Communiquer', 'Anticiper les risques'],
  },
};

// ─── Validation binaire officielle ─────────────────────────────────────────
export const VALIDATION_LEVELS = [
  { key: 'todo',    label: 'À évaluer',           color: '#94a3b8', short: 'À évaluer' },
  { key: 'acquis',  label: 'Compétence acquise',  color: '#22c55e', short: 'Acquis' },
  { key: 'renforcer', label: 'À renforcer',       color: '#f59e0b', short: 'À renforcer' },
];

export function nextValidation(current) {
  const i = VALIDATION_LEVELS.findIndex((v) => v.key === current);
  return VALIDATION_LEVELS[(i < 0 ? 0 : (i + 1) % VALIDATION_LEVELS.length)].key;
}

export const LIVRET_MASTER = {
  key: 'parcours-am-topolino-master-v1',
  meta: {
    title: 'Livret pédagogique — Permis AM',
    subtitle: 'Option quadricycle léger à moteur · Fiat Topolino',
    tagline: 'Support élève + enseignant · aligné arrêté 08/11/2012 modifié',
    version: '2024',
  },
  sequences: SEQUENCES,
  fiches: FICHES,
  evalFinale: EVAL_FINALE_GROUPS,
  reflexes: REFLEXES,
  grille: GRILLE_ENSEIGNANT,
  references: {
    referentiel: "Arrêté du 8 novembre 2012 relatif au brevet de sécurité routière (catégorie AM), annexe II, dispositions en vigueur depuis le 1er mars 2024.",
    vehicule: 'Données constructeur Fiat Topolino : 45 km/h max, 75 km WMTC, recharge 4 h sur prise standard.',
    disclaimer: "Ce livret est un support pédagogique complémentaire. Il ne remplace pas le référentiel réglementaire ni les documents officiels de formation.",
  },
};

export default LIVRET_MASTER;

export function findFiche(id) {
  return FICHES.find((f) => f.id === id) || null;
}
